import { Textarea } from "@mantine/core"
import { useMediaQuery } from "@mantine/hooks"
import React, { useRef, useState } from "react"
import { withNamespaces } from "react-i18next"
import { Navigate, useParams, useNavigate } from "react-router-dom"
import { connect } from "react-redux"
import { Button } from "@quorumus/plum"

import { selectUserLoggedIn } from "QuorumGrassroots/framework/selectors"
import { BillStatusTimeline } from "app/static/frontend/profiles-new/components/BillStatusTimeline"
import TextWithTabs from "app/static/frontend/profiles-new/components/TextWithTabs"
import { timelineStatusFunction } from "app/static/frontend/download-center/containers/status/helpers"

import { Comment, DTVCard, DTVGrid, Icon, WorkflowComponent } from "quorumdesign"
import { ENTERKEY } from "shared/imports/sharedConstants"
import { sanitizeUserCommentTextHtml } from "shared/forms/helperFunctions"
import { reactify } from "app/static/frontend/imports/desktopHelperFunctions"
import {
    useCollaboratorBill,
    useCreateCollaboratorComment,
    useResetCollaboratorBill,
} from "QuorumGrassroots/framework/components/CollaboratorBill/services"
import Timeline from "app/static/frontend/profiles/components/Timeline"
import { reduceTimeline, toDateTimeString } from "shared/imports/sharedHelperFunctions"
import LoadingSpinner from "app/static/frontend/components/LoadingSpinner"

export class ResponseNotOkError extends Error {
    response: Response

    constructor(response: Response, message?: string) {
        super(message || `Network request failed with status ${response.status}`)
        this.response = response
    }
}

const parseHtmlToReact = (html, inlineId?, disableAnchors?, useFullDocument?) => {
    // parse a raw html string into DOM nodes
    const doc = new DOMParser().parseFromString(html, "text/html")
    // grab the nodes from the DOM tree
    // some callers need to be able to parse the full document instead of just the body
    const elements = useFullDocument ? doc.childNodes : doc.body.childNodes
    const react = []

    // IE-compatible NodeList iteration
    // https://developer.mozilla.org/en-US/docs/Web/API/NodeList
    // https://gist.github.com/bendc/4090e383865d81b4b684
    Array.prototype.forEach.call(elements, (element, index) => {
        // convert each DOM node to a react element
        react.push(reactify(element, index, inlineId, disableAnchors))
    })

    // return an array of React elements that we can pass to a React component
    return react
}

const Separator = () => (
    <div
        style={{ marginTop: "24px", marginBottom: "24px", width: "100%", backgroundColor: "#DEE2E6", height: "1px" }}
    />
)

const CollaboratorBillProfile = ({ loggedIn, id, collaboratorBillData }) => {
    const commentAdder = useRef(null)
    const [commentLoading, setCommentLoading] = useState(false)

    const isMobile = useMediaQuery("(max-width: 720px)")

    const invalidateQuery = useResetCollaboratorBill()
    const callback = () => {
        setCommentLoading(false)
        invalidateQuery()
    }
    const createComment = useCreateCollaboratorComment(id, callback)

    const { data: apiData, isLoading: billLoading, isFetching: billFetching } = collaboratorBillData

    const mostRecentText = apiData?.bill_texts[0]
    const textData = {
        ...mostRecentText,
        subtitle: mostRecentText?.pdf_url,
    }
    const loading = billLoading

    const statusId = apiData?.current_general_status
        ? DjangIO.app.bill.status.GeneralBillStatus.byAttribute("key", apiData.current_general_status).value
        : 0
    const billType = apiData?.bill_type
        ? DjangIO.app.bill.models.BillType.byAttribute("key", apiData.bill_type).value
        : 0
    Userdata.current_regions_values = [DjangIO.app.models.Region.byAttribute("key", apiData?.region)?.value || 0]
    const statusTimelineData = timelineStatusFunction(statusId, billType) || []
    const billTextData = [
        {
            aiSummary: mostRecentText?.ai_summary,
            aiSummaryDate: mostRecentText?.ai_summary_processing_at,
            // aiSummarySingleSentence: "AI Key Takeaway goes here",
            currentStatus: apiData?.current_status_text,
            sessionEnd: toDateTimeString(apiData?.session?.end_date, null, true),
            sessionStart: toDateTimeString(apiData?.session?.start_date, null, true),
            statusDate: toDateTimeString(apiData?.current_status_date, null, true),
            statusTimelineData,
            summary: apiData?.summary,
            title: apiData?.title,
        },
    ]
    const navigate = useNavigate()

    const renderComments = () => {
        if (!apiData.comments || !apiData.comments.length) {
            return null
        }
        return apiData.comments.map((comment) => {
            const replacedAndSanitizedCommentText = comment.text

            return (
                <Comment
                    key={comment.id}
                    // circleColor={colorFunctionForIcon(getColorForInitials(comment._extra.user_initials))}
                    circleText={comment.initials}
                    dataCy="comment"
                    date={toDateTimeString(
                        comment.created,
                        null,
                        false,
                        false,
                        false,
                        Userdata.has_international_region,
                    )}
                    name={comment.name}
                    text={parseHtmlToReact(replacedAndSanitizedCommentText)}
                />
            )
        })
    }

    const commentOnKeyDown = (e) => {
        if (e.keyCode === ENTERKEY && !e.shiftKey) {
            e.preventDefault()
            // make sure there is a comment, and is more than just whitespace
            const value = commentAdder.current.value
            if (value && /\S/.test(value)) {
                const sanitizedCommentText = sanitizeUserCommentTextHtml(value)
                commentAdder.current.value = ""
                setCommentLoading(true)
                createComment.mutate(sanitizedCommentText)
            }
        }
    }

    if (!loggedIn) {
        return <Navigate to="/sign_in/" />
    }
    if (!apiData) {
        return <LoadingSpinner />
    }
    const showLoadingComments = commentLoading || billLoading || billFetching

    const timeline = reduceTimeline(apiData.major_actions || [])

    const TextPDFViewer = () => {
        if (loading) {
            return <LoadingSpinner />
        }
        if (!textData?.pdf_url) {
            return (
                <p>
                    <i>No text available currently.</i>
                </p>
            )
        }
        return (
            <TextWithTabs
                data={[textData]}
                getUrl={() => textData.pdf_url}
                title="Title"
                isPDF={true}
                displayFullPDFHeight={true}
            />
        )
    }

    const CommentAdder = () => (
        <WorkflowComponent
            emptyText={loading ? "Loading comments..." : "No comments currently"}
            scroll="reverse"
            title="Add a Comment"
            childInput={
                <div
                    style={{
                        marginTop: "16px",
                        borderTop: "1px solid #7b8a8b",
                    }}
                >
                    <Textarea
                        //disabled={false}
                        onKeyDown={commentOnKeyDown}
                        ref={commentAdder}
                        placeholder={"Write a comment..."}
                    />
                </div>
            }
        >
            {showLoadingComments
                ? "Loading comments..."
                : apiData.comments && apiData.comments.length && renderComments()}
        </WorkflowComponent>
    )

    const SponsorsGrid = () => (
        <>
            <DTVGrid collapsible collapsedGridRows={1} gridWidth={2} headerText="Sponsors">
                {apiData.sponsors && apiData.sponsors.length > 0 ? (
                    apiData.sponsors.map((sponsor) => (
                        <DTVCard
                            key={sponsor.name}
                            imgUrl={sponsor.image_url}
                            title={sponsor.name}
                            text={sponsor.sponsor_type}
                        />
                    ))
                ) : (
                    <div>No Sponsors</div>
                )}
            </DTVGrid>
            <Separator />
        </>
    )
    const CommitteesGrid = () => (
        <>
            <DTVGrid collapsible collapsedGridRows={1} gridWidth={2} headerText="Committees">
                {apiData.committees && apiData.committees.length > 0 ? (
                    apiData.committees.map((committee) => (
                        <DTVCard
                            icon="gavel"
                            key={committee.name}
                            title={committee.search_name}
                            text={`Chair persons: ${committee.chairs_string}`}
                            textIcon="podium"
                        />
                    ))
                ) : (
                    <div>No Committees</div>
                )}
            </DTVGrid>
            <Separator />
        </>
    )

    const TimelineContainer = () => {
        if (!timeline) {
            return null
        }
        return (
            <>
                <Timeline timeline={timeline} />
                <Separator />
            </>
        )
    }
    if (isMobile) {
        return (
            <div
                style={{
                    padding: "20px 25px 60px 25px",
                    display: "flex",
                    flexDirection: "column",
                }}
            >
                <div style={{ display: "flex", alignItems: "center", marginBottom: "16px" }}>
                    <div style={{ flexDirection: "column" }}>
                        <Button size="lg" variant="tertiary" onClick={() => navigate("/")}>
                            <Icon icon="arrow-left" iconFamily="far" size="md" />
                        </Button>
                        <h4 style={{ margin: 0 }}>{apiData.title || "Loading..."}</h4>
                    </div>
                </div>
                <BillStatusTimeline data={billTextData} id={statusId} title={apiData.title} />
                <Separator />
                <TimelineContainer />
                <Separator />
                <SponsorsGrid />
                <CommitteesGrid />
                <CommentAdder />
                <div
                    style={{
                        height: "80 vh",
                        padding: "1rem",
                    }}
                >
                    <TextPDFViewer />
                </div>
            </div>
        )
    }
    return (
        <div
            style={{
                padding: "40px 40px",
                display: "flex",
                flexDirection: "row",
                overflow: "hidden",
            }}
        >
            <div
                style={{
                    flexGrow: 1,
                    flexShrink: 1,
                    paddingRight: "40px",
                    height: "calc(100vh - 125px)",
                    overflow: "auto",
                }}
            >
                <div style={{ display: "flex", alignItems: "center", marginBottom: "16px" }}>
                    <div style={{ flexDirection: "column" }}>
                        <Button size="lg" variant="tertiary" onClick={() => navigate("/")}>
                            <Icon icon="arrow-left" iconFamily="far" size="lg" style={{ fontSize: "1.5em" }} />
                        </Button>
                        <h2 style={{ margin: 0 }}>{apiData.title || "Loading..."}</h2>
                    </div>
                </div>
                <BillStatusTimeline data={billTextData} id={statusId} title={apiData.title} />
                <Separator />
                <TimelineContainer />
                <SponsorsGrid />
                <CommitteesGrid />
                <div
                    style={{
                        height: "80 vh",
                        padding: "1rem",
                    }}
                >
                    <TextPDFViewer />
                </div>
            </div>
            <div
                style={{
                    flexGrow: 0,
                    flexShrink: 0,
                    minWidth: "350px",
                    width: "30%",
                    height: "calc(100vh - 125px)",
                    overflow: "hidden",
                    paddingLeft: "40px",
                }}
            >
                <CommentAdder />
            </div>
        </div>
    )
}

const UnconnectedCollaboratorBill = ({ loggedIn }: { loggedIn: boolean }) => {
    const { id } = useParams()
    const collaboratorBillData = useCollaboratorBill(id, loggedIn)

    if (!loggedIn) {
        return <Navigate to="/sign_in/" />
    }
    if (!collaboratorBillData.data) {
        return <LoadingSpinner />
    }
    if ("forbidden" in collaboratorBillData.data) {
        return <h1>You do not have access to this bill.</h1>
    }
    return <CollaboratorBillProfile loggedIn={loggedIn} id={id} collaboratorBillData={collaboratorBillData} />
}

const mapStateToProps = (state) => ({
    loggedIn: selectUserLoggedIn(state),
})

export const CollaboratorBill = withNamespaces()(connect(mapStateToProps, null)(UnconnectedCollaboratorBill))
